import {
  ApolloError,
  LazyQueryHookOptions,
  LazyQueryResult,
  QueryLazyOptions,
  TypedDocumentNode,
  useLazyQuery,
} from '@apollo/client'
import { DocumentNode } from 'graphql'
import { useCallback, useState } from 'react'

import { useAuth } from '../lib/auth'

export type ApiLazyQueryTuple<TData, TVariables> = [
  (options?: QueryLazyOptions<TVariables>) => void,
  Omit<LazyQueryResult<TData, TVariables>, 'error'> & {
    error?: ApolloError | Error
  },
]

export type UseApiLazyQueryOptions<TData, TVariables> = LazyQueryHookOptions<TData, TVariables> & {
  loginRequired?: boolean
}

const useApiLazyQuery = <TData = unknown, TVariables = Record<string, unknown>>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  { loginRequired = false, ...hookOptions }: UseApiLazyQueryOptions<TData, TVariables> = {},
): ApiLazyQueryTuple<TData, TVariables> => {
  const { getAccessTokenSilently } = useAuth()
  const [tokenError, setTokenError] = useState<Error | null>(null)
  const [runLazyQuery, { error: queryError, ...queryResultProps }] = useLazyQuery(
    query,
    hookOptions,
  )

  const runApiLazyQuery = useCallback(
    async (options?: QueryLazyOptions<TVariables>) => {
      setTokenError(null)
      let accessToken: string | undefined
      try {
        accessToken = await getAccessTokenSilently()
      } catch (accessTokenError) {
        // ignore error if token not required and do request without token
        if (loginRequired) {
          setTokenError(
            accessTokenError instanceof Error ? accessTokenError : new Error(`${accessTokenError}`),
          )
        }
      }
      runLazyQuery({
        ...options,
        variables: options?.variables,
        context: {
          ...(options && options.context),
          uri: `/api/graphql`,
          token: accessToken,
        },
      })
    },
    [runLazyQuery, getAccessTokenSilently, loginRequired],
  )

  return [runApiLazyQuery, { ...queryResultProps, error: tokenError || queryError }]
}

export default useApiLazyQuery
